/**
 * Where a category_page entry lives on the site.
 *
 * Contentstack stores every category_page url without a prefix (`/Video-Cards`,
 * `/Services/SmartHands-Services`, `/company/about-us`). The site adds `/category` to
 * single-segment urls only:
 *
 *   `/Video-Cards`                  -> `/category/Video-Cards`
 *   `/Services/SmartHands-Services` -> `/Services/SmartHands-Services`  (nested: served as-is)
 *   `/company/about-us`             -> `/company/about-us`              (nested: served as-is)
 *
 * Nested entries are registered as root routes by CategoryService.initRoutes, so the router
 * already serves them at their bare url. These helpers keep the guards, link builders and the
 * express redirect middleware on the same rule instead of each carrying a `Services/` special case.
 *
 * Pure functions, no Angular imports, so the SSR middleware can use them too.
 */

const segmentsOf = (url: string): string[] =>
    String(url || '')
        .split('?')[0]
        .split('#')[0]
        .split('/')
        .filter(Boolean);

/**
 * @function stripCategoryPrefix
 * @description The part of a pathname that is compared against CategoryService.paths.
 *   `/category/Video-Cards`   -> `Video-Cards`
 *   `/company/about-us`       -> `company/about-us`
 *   `/category/search?q=gpu`  -> `search`
 * @param {string} path a pathname, with or without the `/category` prefix
 * @returns {string} the slug (single segment) or nested path (several segments), no leading slash
 */
export function stripCategoryPrefix(path: string): string {
    const segments = segmentsOf(path);
    if (segments[0] === 'category') {
        segments.shift();
    }
    return segments.join('/');
}

/**
 * @function isNestedCategoryUrl
 * @description True when a url has two or more path segments, i.e. a category_page entry
 * that is served at its bare url rather than under `/category`.
 * @param {string} url a CMS url or a pathname
 * @returns {boolean}
 */
export function isNestedCategoryUrl(url: string): boolean {
    return segmentsOf(url).length > 1;
}

/**
 * @function toPublicCategoryUrl
 * @description The href to use when linking to a category_page entry by its CMS url.
 * Already-prefixed and nested urls are returned unchanged.
 * @param {string} url the entry's `url` field, with or without a leading slash
 * @returns {string} the public path; `/` when the url is empty
 */
export function toPublicCategoryUrl(url: string): string {
    if (!url) {
        return '/';
    }
    if (url.startsWith('/category/') || isNestedCategoryUrl(url)) {
        return url;
    }
    return `/category${url.startsWith('/') ? url : '/' + url}`;
}
